import { apple, type Keys, type Press, isModifierOnly, strokeMatches } from "./keys.ts"

/*
 * Which shortcut a key press is, if any: the pure half of the listener in `use-shortcut.ts`, which
 * hands it the shortcuts mounted now and what the press landed on.
 */

/** How long the second key of a chord may take after the first: G, then P within this. */
export const chordTimeout = 1500

/** The first key of a chord, pressed and waiting for its second. */
export type Pending = { press: Press; at: number }

/** A shortcut that could fire: its keys, and whatever the listener runs if it does. */
export type Candidate<T> = { keys: readonly Keys[]; value: T }

/**
 * What a press does: the candidates it fires, best first — the listener runs them in turn until
 * one takes it — and the chord it leaves waiting, if it began one.
 *
 * A press completing a waiting chord fires that chord and nothing else. One that fails to complete
 * it fires nothing either, since it was meant as the chord's second key. A press that begins a
 * chord waits for its second key, even where a single-key shortcut would have matched it.
 */
export function step<T>(
  candidates: readonly Candidate<T>[],
  press: Press,
  pending: Pending | undefined,
  now: number,
  onApple = apple,
): { fire: T[]; pending?: Pending } {
  // A modifier going down on its own is the start of the next stroke, not a stroke of its own.
  if (isModifierOnly(press)) return { fire: [], ...(pending && { pending }) }
  const matching = (test: (keys: Keys) => boolean) =>
    candidates.filter(({ keys }) => keys.some(test)).map(({ value }) => value)

  if (pending && now - pending.at <= chordTimeout)
    return {
      fire: matching(
        ([first, second]) =>
          second !== undefined &&
          strokeMatches(first, pending.press, onApple) &&
          strokeMatches(second, press, onApple),
      ),
    }

  const beginsChord = matching(
    ([first, second]) => second !== undefined && strokeMatches(first, press, onApple),
  )
  if (beginsChord.length > 0) return { fire: [], pending: { press, at: now } }

  return {
    fire: matching(([only, second]) => second === undefined && strokeMatches(only, press, onApple)),
  }
}

/** Where a binding stands against another of the same keys. */
export type Standing = {
  /** Scoped to an element (`within`) that the press landed inside. */
  inside: boolean
  /** Bound to yield to every other binding, as the shell's `/` yields to a page's search field. */
  fallback: boolean
  /** When it mounted: a later one has a higher number. */
  order: number
}

/**
 * Orders bindings best first, for `step`'s candidates: one scoped to where the press landed, as the
 * more particular; then any that isn't a fallback; then the latest mounted, as a page's own
 * overrides one mounted before it.
 */
export const byStanding = (a: Standing, b: Standing) =>
  Number(b.inside) - Number(a.inside) ||
  Number(a.fallback) - Number(b.fallback) ||
  b.order - a.order

/** What a press landed on, as far as whether a shortcut may take it. */
export type Landing = {
  /** A field the Member types into: an input, a textarea, a select, or rich text. */
  editable: boolean
  /** A control Enter or Space already works: a button, a link, a checkbox, a menu item. */
  activatable: boolean
  /** Whether a dialog, popover, menu or listbox is open over the page. */
  overlayOpen: boolean
}

/**
 * Whether a mounted shortcut may take a press. `inside` is whether the press landed inside the
 * element the shortcut is scoped to, or undefined for a shortcut of the whole page.
 *
 * A page's shortcut stands aside while anything is open over the page, so a key pressed in a
 * dialog is the dialog's; a scoped one fires only inside its element, wherever that is. Neither
 * takes a key typed into a field unless its registry entry says it works in fields (⌘↵ does, C
 * doesn't), nor Enter or Space on a control those keys already work.
 */
export function admits(
  shortcut: { inFields?: boolean },
  press: Press,
  landing: Landing,
  inside: boolean | undefined,
) {
  if (inside === false) return false
  if (inside === undefined && landing.overlayOpen) return false
  if (landing.editable && !shortcut.inFields) return false
  const bare = !press.metaKey && !press.ctrlKey && !press.altKey
  if (landing.activatable && bare && (press.key === "Enter" || press.key === " ")) return false
  return true
}

/** The parts of an element that say what kind of target it is. An `HTMLElement` is one. */
export type TargetLike = {
  tagName: string
  isContentEditable?: boolean
  getAttribute(name: string): string | null
}

// Inputs that take a click rather than typing: a key pressed on one is not text.
const clickInputs = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
])

/** Whether a target is somewhere the Member types: a text input, a textarea, a select, rich text. */
export function isEditable(target: TargetLike | null) {
  if (!target) return false
  if (target.isContentEditable) return true
  const tag = target.tagName.toLowerCase()
  if (tag === "textarea" || tag === "select") return true
  if (tag !== "input") return false
  const type = (target.getAttribute("type") ?? "text").toLowerCase()
  return !clickInputs.has(type)
}

const activatableRoles = new Set([
  "button",
  "link",
  "checkbox",
  "radio",
  "switch",
  "tab",
  "option",
  "menuitem",
  "menuitemcheckbox",
  "menuitemradio",
])

/** Whether a target is a control that Enter or Space already works. */
export function isActivatable(target: TargetLike | null) {
  if (!target) return false
  const tag = target.tagName.toLowerCase()
  if (tag === "button" || tag === "a" || tag === "summary") return true
  if (tag === "input") return clickInputs.has((target.getAttribute("type") ?? "").toLowerCase())
  return activatableRoles.has(target.getAttribute("role") ?? "")
}
